import React from "react";
import PropTypes from "prop-types";
import { Dropdown, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import Flex from "components/common/flex";
import SubtleBadge from "components/common/subtleBadge";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { getCategoryConfig } from "./globalSearchBox";


const MAX_RESULTS = 3;

const Highlight = ({ text, query }) => {
    if (!query || !text) return <>{text}</>;
    const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const parts   = String(text).split(new RegExp(`(${escaped})`, "gi"));
    return (
        <>
            {parts.map((part, i) =>
                part.toLowerCase() === query.toLowerCase()
                    ? <mark key={i} style={{ backgroundColor: "#fff3cd", padding: "0 1px", borderRadius: "2px", fontWeight: 600 }}>{part}</mark>
                    : part
            )}
        </>
    );
};

Highlight.propTypes = {
    text:  PropTypes.string,
    query: PropTypes.string,
};

const useResultNavigation = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const culture  = localStorage.getItem("culture") || "en";

    return (item, type) => {
        try {
            switch (type) {

                // ── Orders: set LocationId then navigate ──────────────────────
                case "orders": {
                    if (item.Location) {
                        localStorage.setItem("LocationId", item.Location);
                    }
                    navigate(
                        `/orders/${item.OrderId}/details-order?culture=${culture}`
                    );
                    break;
                }

                // ── Proposals: same simple pattern as relations ───────────────
                case "proposals": {
                    if (item.LocationId) {
                        localStorage.setItem("LocationId", item.LocationId);
                    }
                    navigate(
                        `/proposals/${item.ProposalId}/details-proposal?culture=${culture}`
                    );
                    break;
                }

                // ── Relations: set LocationId then navigate ───────────────────
                case "relations": {
                    if (item.LocationId) {
                        localStorage.setItem("LocationId", item.LocationId);
                    }
                    const targetPath = `/relations/${item.RelationId}/details-relation?culture=${culture}`;
                    const isOnRelationDetails =
                        location.pathname.includes("/relations/") &&
                        location.pathname.includes("/details-relation");

                    if (isOnRelationDetails) {
                        navigate("/relations", { replace: true });
                        setTimeout(() => navigate(targetPath), 0);
                    } else {
                        navigate(targetPath);
                    }
                    break;
                }

                // ── Users: navigate directly ──────────────────────────────────
                case "users": {
                    navigate(
                        `/users/${item.UserId}/details-user?culture=${culture}`
                    );
                    break;
                }

                default:
                    break;
            }
        } catch (error) {
            console.error("[GlobalSearch] Navigation error:", error);
        }
    };
};

// ── Single common item component ──────────────────────────────────────────────
const SearchResultItem = ({ item, type, query, onNavigate }) => {
    const { t } = useTranslation();
    const CATEGORY_CONFIG = getCategoryConfig(t);
    const config      = CATEGORY_CONFIG[type];
    const avatarLabel = config.getAvatarLabel ? config.getAvatarLabel(item) : config.avatarLabel;
    const badge       = config.getBadge ? config.getBadge(item) : null;
    const right       = config.getRight ? config.getRight(item) : null;
    const title       = config.getTitle(item);
    const subtitle    = config.getSubtitle(item);

    return (
        <Dropdown.Item
            className="px-x1 py-2 focus-ring-0 outline-0"
            style={{ cursor: "pointer" }}
            onClick={() => onNavigate(item, type)}
        >
            <Flex alignItems="center" justifyContent="between">
                <Flex alignItems="center" className="gap-2">
                    <div className="avatar avatar-s">
                        <div className={`avatar-name rounded-circle ${config.avatarBg} d-flex align-items-center justify-content-center`}>
                            <span className={`fs-11 fw-bold ${config.avatarText}`}>{avatarLabel}</span>
                        </div>
                    </div>
                    <div>
                        <h6 className="mb-0 fs-11 fw-semi-bold">
                            <Highlight text={title} query={query} />
                        </h6>
                        <p className="fs-11 mb-0 text-500">
                            <Highlight text={subtitle} query={query} />
                        </p>
                    </div>
                </Flex>
                {badge && (
                    <SubtleBadge pill bg={badge.variant}>
                        {badge.label}
                    </SubtleBadge>
                )}
                {right && <span className="fs-11 text-500">{right}</span>}
            </Flex>
        </Dropdown.Item>
    );
};

SearchResultItem.propTypes = {
    item:       PropTypes.object.isRequired,
    type:       PropTypes.oneOf(["orders", "proposals", "relations", "users"]).isRequired,
    query:      PropTypes.string,
    onNavigate: PropTypes.func.isRequired,
};

// ── SectionHeader — unchanged
const SectionHeader = ({ title }) => (
    <div className="px-x1 pt-2 pb-1">
        <Flex justifyContent="between" alignItems="center">
            <p className="mb-0 fs-11 fw-bold text-500">{title}</p>
        </Flex>
        <hr className="my-1" />
    </div>
);

SectionHeader.propTypes = { title: PropTypes.string.isRequired };

// ── ViewAllItem — updated to support View Less
const ViewAllItem = ({ label, onClick, isExpanded }) => {
    const { t } = useTranslation();
    return (
        <Dropdown.Item
            className="px-x1 py-1 text-center text-primary fs-11 fw-semi-bold"
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClick();
            }}
        >
            <Flex justifyContent="center" alignItems="center" className="gap-1">
                <span>{isExpanded ? t("viewLess") : t("viewAll")} {label}</span>
                <FontAwesomeIcon
                    icon={faChevronRight}
                    className={`fs-11 ${isExpanded ? "rotate-icon" : ""}`}
                />
            </Flex>
        </Dropdown.Item>
    );
};

ViewAllItem.propTypes = {
    label:      PropTypes.string.isRequired,
    onClick:    PropTypes.func.isRequired,
    isExpanded: PropTypes.bool,
};

// ── ResultSection — updated to toggle View All / View Less
const ResultSection = ({ title, items, type, expandedSection, setExpandedSection, sectionKey, query, onNavigate }) => (
    <>
        <SectionHeader title={title} />
        {(expandedSection === sectionKey ? items : items.slice(0, MAX_RESULTS)).map((item, idx) => (
            <SearchResultItem
                key={`${item.OrderId ?? item.ProposalId ?? item.RelationId ?? item.UserId}-${idx}`}
                item={item}
                type={type}
                query={query}
                onNavigate={onNavigate}
            />
        ))}
        {items.length > MAX_RESULTS && (
            <ViewAllItem
                label={title}
                isExpanded={expandedSection === sectionKey}
                onClick={() => setExpandedSection(expandedSection === sectionKey ? null : sectionKey)}
            />
        )}
    </>
);

ResultSection.propTypes = {
    title:              PropTypes.string.isRequired,
    items:              PropTypes.array.isRequired,
    type:               PropTypes.string.isRequired,
    expandedSection:    PropTypes.string,
    setExpandedSection: PropTypes.func.isRequired,
    sectionKey:         PropTypes.string.isRequired,
    query:              PropTypes.string,
    onNavigate:         PropTypes.func.isRequired,
};

// ── Main SearchResults ────────────────────────────────────────────────────────
const SearchResults = ({ results, loading, expandedSection, setExpandedSection, searchInputValue, t, onClose }) => {

    const navigateTo = useResultNavigation();

    // close dropdown + navigate on item click
    const handleNavigate = (item, type) => {
        onClose?.();
        navigateTo(item, type);
    };

    // ── Loading state ─────────────────────────────────────────────────────────
    if (loading) {
        return (
            <div className="text-center py-4">
                <Spinner animation="border" size="sm" variant="primary" className="me-2" />
            </div>
        );
    }

    const hasResults =
        results &&
        (results.orders?.length > 0 ||
            results.proposals?.length > 0 ||
            results.relations?.length > 0 ||
            results.users?.length > 0);

    return (
        <>
            {!hasResults && searchInputValue.trim().length >= 3 && (
                <div className="text-center py-4">
                    <FontAwesomeIcon icon={faSearch} className="text-300 fs-4 mb-2" />
                    <p className="fs-11 mb-1 fw-semi-bold text-500">
                        {t("noResultsFor")} &quot;{searchInputValue}&quot;
                    </p>
                    <p className="fs-11 mb-0 text-400">{t("tryDifferentKeywords")}</p>
                </div>
            )}

            {results?.orders?.length > 0 && (
                <ResultSection
                    title={t("orders")}
                    items={results.orders}
                    type="orders"
                    sectionKey="orders"
                    expandedSection={expandedSection}
                    setExpandedSection={setExpandedSection}
                    query={searchInputValue}
                    onNavigate={handleNavigate}
                />
            )}

            {results?.proposals?.length > 0 && (
                <ResultSection
                    title={t("proposals")}
                    items={results.proposals}
                    type="proposals"
                    sectionKey="proposals"
                    expandedSection={expandedSection}
                    setExpandedSection={setExpandedSection}
                    query={searchInputValue}
                    onNavigate={handleNavigate}
                />
            )}

            {results?.relations?.length > 0 && (
                <ResultSection
                    title={t("relations")}
                    items={results.relations}
                    type="relations"
                    sectionKey="relations"
                    expandedSection={expandedSection}
                    setExpandedSection={setExpandedSection}
                    query={searchInputValue}
                    onNavigate={handleNavigate}
                />
            )}

            {results?.users?.length > 0 && (
                <ResultSection
                    title={t("users")}
                    items={results.users}
                    type="users"
                    sectionKey="users"
                    expandedSection={expandedSection}
                    setExpandedSection={setExpandedSection}
                    query={searchInputValue}
                    onNavigate={handleNavigate}
                />
            )}
        </>
    );
};

SearchResults.propTypes = {
    results:            PropTypes.object,
    loading:            PropTypes.bool,
    expandedSection:    PropTypes.string,
    setExpandedSection: PropTypes.func.isRequired,
    searchInputValue:   PropTypes.string.isRequired,
    t:                  PropTypes.func.isRequired,
    onClose:            PropTypes.func,
};

export default SearchResults;